import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { foodProducts, industrialProducts } from '../mock';
import { useSearchParams } from 'react-router-dom';

const Products = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'industrial' ? 'industrial' : 'food');

  useEffect(() => {
    if (tabParam === 'industrial') {
      setActiveTab('industrial');
    }
  }, [tabParam]);

  const ProductCard = ({ product }) => (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group">
      <div className="relative overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name} 
          className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-[#02029E] shadow-md">
          {product.category}
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h3>
        <p className="text-gray-600 leading-relaxed">{product.description}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-red-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Our Products</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Explore our comprehensive range of premium food products and industrial packaging solutions
            </p>
          </div>
        </div>
      </section>

      {/* Products Tabs */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 h-14 bg-white shadow-md rounded-lg p-1">
              <TabsTrigger 
                value="food" 
                className="text-base font-semibold data-[state=active]:bg-[#02029E] data-[state=active]:text-white rounded-md transition-all duration-200"
              >
                Food Products
              </TabsTrigger>
              <TabsTrigger 
                value="industrial" 
                className="text-base font-semibold data-[state=active]:bg-[#D41414] data-[state=active]:text-white rounded-md transition-all duration-200"
              >
                Industrial Packaging
              </TabsTrigger>
            </TabsList>

            {/* Food Products Tab */}
            <TabsContent value="food" className="mt-0">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Premium Food Products</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  Authentic olives and olive products from our trusted partner in Gimlek, Bursa
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {foodProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </TabsContent>

            {/* Industrial Products Tab */}
            <TabsContent value="industrial" className="mt-0">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Industrial Packaging Solutions</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  Comprehensive range of packaging materials and adhesive solutions from global suppliers
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industrialProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Interested in Our Products?</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Contact us today to discuss your requirements and get a customized quote
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#02029E] text-white font-semibold rounded-lg hover:bg-[#02029E]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default Products;
